import React, { useEffect, useState, useCallback } from "react";
import API from "../../../services/api";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [showPass, setShowPass] = useState(false);

  const emptyForm = {
    name: "",
    username: "",
    email: "",
    mobile_number: "",
    role_id: "2",
    password: "",
    confirm_password: "",
    active: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  // ── Fetch Roles ───────────────────────────────────────────
  const fetchRoles = useCallback(async () => {
    try {
      const res = await API.get("/users/roles");
      // Exclude Admin role from creation
      const filtered = res.data.data.filter((r) => r.rolename !== "Admin");
      setRoles(filtered);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Users ───────────────────────────────────────────
  const fetchUsers = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/users/", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            role_id: filterRole || undefined,
          },
        });
        setUsers(res.data.data.users);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, filterRole],
  );

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  // ── Validate Form ─────────────────────────────────────────
  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.username.trim()) errors.username = "Username is required";
    if (form.username.length < 4) errors.username = "Min 4 characters";
    if (form.mobile_number && !/^\d{10}$/.test(form.mobile_number)) {
      errors.mobile_number = "Enter valid 10 digit mobile";
    }
    if (!editItem) {
      if (!form.password) errors.password = "Password is required";
      if (form.password.length < 6) errors.password = "Min 6 characters";
      if (form.password !== form.confirm_password) {
        errors.confirm_password = "Passwords do not match";
      }
    } else {
      if (form.password && form.password.length < 6) {
        errors.password = "Min 6 characters";
      }
      if (form.password && form.password !== form.confirm_password) {
        errors.confirm_password = "Passwords do not match";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Open Add ──────────────────────────────────────────────
  const openAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setFormErrors({});
    setShowPass(false);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────
  const openEdit = (user) => {
    setEditItem(user);
    setFormErrors({});
    setShowPass(false);
    setForm({
      name: user.name || "",
      username: user.username || "",
      email: user.email || "",
      mobile_number: user.mobile || "",
      role_id: String(user.role_id),
      password: "",
      confirm_password: "",
      active: user.active,
    });
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        username: form.username,
        email: form.email || null,
        mobile_number: form.mobile_number || null,
        role_id: parseInt(form.role_id),
        active: form.active,
      };
      if (form.password) {
        payload.password = form.password;
      }
      if (editItem) {
        await API.put(`/users/${editItem.id}`, payload);
      } else {
        await API.post("/users/", payload);
      }
      setShowModal(false);
      fetchUsers(currentPage);
    } catch (err) {
      const msg = err.response?.data?.message || "Error saving user";
      setFormErrors({ api: msg });
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle Active ─────────────────────────────────────────
  const toggleActive = async (user) => {
    setToggling(user.id);
    try {
      await API.put(`/users/${user.id}`, {
        name: user.name,
        role_id: user.role_id,
        active: !user.active,
      });
      fetchUsers(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setToggling(null);
    }
  };

  const roleBadge = (role) => {
    const colors = {
      Admin: "bg-danger-subtle text-danger",
      Manager: "bg-warning-subtle text-warning",
      Staff: "bg-info-subtle text-info",
    };
    return colors[role] || "bg-secondary-subtle text-secondary";
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">User Management</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> users
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={openAdd}
        >
          <i className="bi bi-person-plus me-2"></i>Create User
        </button>
      </div>

      {/* Info Banner */}
      <div
        className="alert border-0 rounded-4 mb-3 small"
        style={{ background: "#e8f0fe", color: "#1a3c5e" }}
      >
        <i className="bi bi-info-circle me-2"></i>
        <strong>Manager</strong> can view & update grievance status, reassign to
        Staff. &nbsp;<strong>Staff</strong> can view grievances assigned to them
        and update status.
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, username, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <select
                className="form-select"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <option value="">All Roles</option>
                {roles.map((r) => (
                  <option key={r.roleid} value={r.roleid}>
                    {r.rolename}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => fetchUsers(1)}
              >
                <i className="bi bi-search me-1"></i>Search
              </button>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearch("");
                  setFilterRole("");
                  fetchUsers(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-2"></i>
              No users found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Name</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Username
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Mobile
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Role</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Status
                    </th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                            style={{
                              width: 34,
                              height: 34,
                              fontSize: "13px",
                              background:
                                "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                            }}
                          >
                            {u.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold small">{u.name}</div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {u.email || ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border small fw-normal">
                          @{u.username}
                        </span>
                      </td>
                      <td className="small text-muted">{u.mobile || "—"}</td>
                      <td>
                        <span className={`badge small ${roleBadge(u.role)}`}>
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={u.active}
                            onChange={() => toggleActive(u)}
                            disabled={toggling === u.id}
                            style={{ cursor: "pointer" }}
                          />
                          <label className="form-check-label small text-muted">
                            {u.active ? "Active" : "Inactive"}
                          </label>
                        </div>
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openEdit(u)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchUsers(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchUsers(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchUsers(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <div>
                  <h5 className="modal-title fw-bold">
                    {editItem ? "Edit User" : "Create New User"}
                  </h5>
                  <p className="text-muted small mb-0">
                    {editItem
                      ? "Update user details. Leave password blank to keep existing."
                      : "Fill all fields to create a new user account."}
                  </p>
                </div>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSave}>
                <div
                  className="modal-body px-4 py-3"
                  style={{ maxHeight: "65vh", overflowY: "auto" }}
                >
                  {/* API Error */}
                  {formErrors.api && (
                    <div className="alert alert-danger py-2 small mb-3">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {formErrors.api}
                    </div>
                  )}

                  {/* Full Name */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                      placeholder="Enter full name"
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                    {formErrors.name && (
                      <div className="invalid-feedback">{formErrors.name}</div>
                    )}
                  </div>

                  {/* Username */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Username <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">@</span>
                      <input
                        type="text"
                        className={`form-control ${formErrors.username ? "is-invalid" : ""}`}
                        placeholder="username (min 4 chars)"
                        value={form.username}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            username: e.target.value
                              .toLowerCase()
                              .replace(/\s/g, ""),
                          })
                        }
                        disabled={!!editItem}
                      />
                      {formErrors.username && (
                        <div className="invalid-feedback">
                          {formErrors.username}
                        </div>
                      )}
                    </div>
                    {editItem && (
                      <div className="form-text text-muted">
                        Username cannot be changed
                      </div>
                    )}
                  </div>

                  {/* Mobile + Email */}
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Mobile
                      </label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.mobile_number ? "is-invalid" : ""}`}
                        placeholder="10 digit mobile"
                        maxLength={10}
                        value={form.mobile_number}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setForm({ ...form, mobile_number: val });
                        }}
                      />
                      {formErrors.mobile_number && (
                        <div className="invalid-feedback">
                          {formErrors.mobile_number}
                        </div>
                      )}
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">
                        Email
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="email@example.com"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Role <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={form.role_id}
                      onChange={(e) =>
                        setForm({ ...form, role_id: e.target.value })
                      }
                    >
                      {roles.map((r) => (
                        <option key={r.roleid} value={r.roleid}>
                          {r.rolename}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      {form.role_id === "2"
                        ? "⚠️ Manager can view/update grievances and reassign to Staff"
                        : "👤 Staff can view grievances assigned to them and update status"}
                    </div>
                  </div>

                  {/* Password */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Password{" "}
                      {!editItem && <span className="text-danger">*</span>}
                      {editItem && (
                        <span className="text-muted">
                          (leave blank to keep current)
                        </span>
                      )}
                    </label>
                    <div className="input-group">
                      <input
                        type={showPass ? "text" : "password"}
                        className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                        placeholder="Min 6 characters"
                        value={form.password}
                        onChange={(e) =>
                          setForm({ ...form, password: e.target.value })
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPass(!showPass)}
                      >
                        <i
                          className={`bi ${showPass ? "bi-eye-slash" : "bi-eye"}`}
                        ></i>
                      </button>
                      {formErrors.password && (
                        <div className="invalid-feedback">
                          {formErrors.password}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Confirm Password
                    </label>
                    <input
                      type={showPass ? "text" : "password"}
                      className={`form-control ${formErrors.confirm_password ? "is-invalid" : ""}`}
                      placeholder="Re-enter password"
                      value={form.confirm_password}
                      onChange={(e) =>
                        setForm({ ...form, confirm_password: e.target.value })
                      }
                    />
                    {formErrors.confirm_password && (
                      <div className="invalid-feedback">
                        {formErrors.confirm_password}
                      </div>
                    )}
                  </div>

                  {/* Active Toggle */}
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="activeToggle"
                      checked={form.active}
                      onChange={(e) =>
                        setForm({ ...form, active: e.target.checked })
                      }
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="activeToggle"
                    >
                      Account Active
                    </label>
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn text-white px-4"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {editItem ? "Update User" : "Create User"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
